import { lineStockChip, type OrderStatus, type StockChip } from '@wms/shared';

const LIVE_STOCK_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'DRAFT',
  'SUBMITTED',
  'ON_HOLD',
  'IN_ASSEMBLY',
]);

/**
 * Stock is actionable only until assembly finishes. Afterwards the order has
 * already consumed/produced its goods, so a submit-time shortage badge is
 * historical information rather than a current warning (WMS-60).
 */
export function shouldShowLiveLineStock(status: OrderStatus): boolean {
  return LIVE_STOCK_STATUSES.has(status);
}

/** Live tone for one submitted order line, using this order's stock allocation. */
export function liveOrderLineStockChip(input: {
  productSkuId: string;
  lineQty: number;
  fromStockQty: number;
  lineIsLeaf: boolean;
  bom: readonly { componentSkuId: string; qtyRequired: number }[];
  available: Readonly<Record<string, number>>;
}): StockChip {
  const requirements = new Map<string, number>();
  if (input.lineIsLeaf) {
    requirements.set(input.productSkuId, input.lineQty);
  } else {
    const fromStock = Math.min(input.lineQty, Math.max(0, input.fromStockQty));
    if (fromStock > 0) requirements.set(input.productSkuId, fromStock);
    const toBuild = Math.max(0, input.lineQty - fromStock);
    for (const line of input.bom) {
      requirements.set(
        line.componentSkuId,
        (requirements.get(line.componentSkuId) ?? 0) + toBuild * line.qtyRequired,
      );
    }
  }

  const rank: Record<StockChip, number> = { green: 0, unknown: 1, amber: 2, red: 3 };
  let worst: StockChip = 'green';
  for (const [skuId, required] of requirements) {
    const tone = lineStockChip({ required, available: input.available[skuId] });
    if (rank[tone] > rank[worst]) worst = tone;
  }
  return worst;
}
